import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const enappgyMetaDataServiceApi = createApi({
  reducerPath: "enappgyMetaDataServiceApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_METADATA_URL
  }),
  tagTypes: [
    "Buildings",
    "Areas",
    "Favorites",
    "User",
    "UserBuildings",
    "BuildingsMetadata",
    "SensorTypes",
    "Modules",
    "Users",
    "Tenants",
    "Profiles",
    "Alerts",
    "TenantsCatalog",
    "AlertsSettings",
    "UserMetadata",
    "ProfilesModules",
    "Reports",
    "UserTenants",
    "AlertsBuilding",
    "UsersByTenant",
    "BuildingsById",
    "Notifications"
  ],

  endpoints: (builder) => ({
    //Building Queries
    getBuildingByTenantWithFloorsAreas: builder.query({
      query: (tenantId) => `Buildings/GetByTenantIdWithFloorsAreas/${tenantId}`,
      providesTags: ["Buildings"]
    }),
    getBuildingByTenant: builder.query({
      query: (tenantId) => `Buildings/GetByTenantIdWithFloors/${tenantId}`,
      providesTags: ["Buildings"]
    }),

    getBuildingById: builder.query({
      query: (buildingId) => `Buildings/${buildingId}`,
      providesTags: ["BuildingsById"]
    }),
    getBuildingWithFloorsById: builder.query({
      query: (buildingId) => `Buildings/${buildingId}/GetWithFloors`,
      providesTags: ["BuildingsWithFloorsById"]
    }),
    getBuildingByUserId: builder.query({
      query: (payload) =>
        `UserBuildings/GetByUserIdTenantIdWithProfiles?userId=${payload.userId}&tenantId=${payload.tenantId}`,
      providesTags: ["UserBuildings"]
    }),
    getBuildingMetadataByTenantId: builder.query({
      query: (tenantId) => `Buildings/GetByTenantId/${tenantId}`,
      providesTags: ["BuildingsMetadata"]
    }),
    editBuilding: builder.mutation({
      query: (payload) => ({
        url: `Buildings/${payload.id}/Update`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["BuildingsById", "BuildingsMetadata", "Buildings"]
    }),
    addBuildingModule: builder.mutation({
      query: (payload) => ({
        url: `BuildingModules/UpdateBuildingModules?buildingId=${payload[0].buildingId}`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["Modules", "Buildings", "UserBuildings", "BuildingsMetadata"]
    }),
    getBuildingModules: builder.query({
      query: (buildingId) => `BuildingModules/GetByBuildingId/${buildingId}`,
      providesTags: ["Modules"]
    }),
    deleteBuildingModule: builder.mutation({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join("&");
        return {
          url: `BuildingModules/Delete?${queryString}`,
          method: "POST"
        };
      },
      invalidatesTags: ["Modules", "Buildings", "UserBuildings", "BuildingsMetadata"]
    }),
    getBuildingSensorTypes: builder.query({
      query: (buildingId) => `BuildingSensorTypes/GetByBuildingId/${buildingId}`,
      providesTags: ["SensorTypes"]
    }),
    addBuildingSensorType: builder.mutation({
      query: (payload) => ({
        url: `BuildingSensorTypes/UpdateBuildingSensorTypes?buildingId=${payload[0].buildingId}`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["SensorTypes", "Buildings", "UserBuildings", "BuildingsMetadata"]
    }),
    deleteBuildingSensorType: builder.mutation({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join("&");
        return {
          url: `BuildingSensorTypes/Delete?${queryString}`,
          method: "POST"
        };
      },
      invalidatesTags: ["SensorTypes", "Buildings", "UserBuildings", "BuildingsMetadata"]
    }),

    //Areas Queries
    getAreasByFloor: builder.query({
      query: (floorId) => `Areas/GetByFloorId/${floorId}`,
      providesTags: ["Areas"]
    }),

    // Favorites Queries
    getFavoritesByUserId: builder.query({
      query: (payload) =>
        `MultisenseFavorites/GetByUserTenantId?userId=${payload.userId}&tenantId=${payload.tenantId}`,
      providesTags: ["Favorites"]
    }),
    addFavorite: builder.mutation({
      query: (payload) => ({
        url: `MultisenseFavorites`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["Favorites"]
    }),
    updateFavorites: builder.mutation({
      query: (payload) => ({
        url: `/MultisenseFavorites/${payload.id}/Update`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["Favorites"]
    }),
    deleteFavorite: builder.mutation({
      query: (payload) => ({
        url: `MultisenseFavorites/${payload}/Delete`,
        method: "POST"
      }),
      invalidatesTags: ["Favorites"]
    }),

    // Tenant Queries
    getTenants: builder.query({
      query: () => "Tenants",
      providesTags: ["Tenants"]
    }),
    getTenantsWithBuildingProfiles: builder.query({
      query: () => "Tenants/GetWithBuildingsProfiles",
      providesTags: ["TenantsCatalog"]
    }),
    updateTenantName: builder.mutation({
      query: (payload) => ({
        url: `Tenants/${payload.tenantId}/UpdateDescription?description=${payload.description}`,
        method: "POST"
      }),
      invalidatesTags: ["TenantsCatalog", "Tenants"]
    }),

    // Users Queries
    getUserById: builder.query({
      query: (userId) => `Users/${userId}/GetUserComplete`,
      providesTags: ["User"]
    }),

    getAuth0Users: builder.query({
      query: () => "Users/GetAuth0UsersComplete",
      providesTags: ["Users"]
    }),
    getAuth0UsersByTenant: builder.query({
      query: (tenantId) => `Users/GetAuth0UsersByTenantId?tenantId=${tenantId}`,
      providesTags: ["UsersByTenant"]
    }),
    getAuth0UsersByBuilding: builder.query({
      query: (buildingId) => `Users/GetAuth0UsersCompleteByBuildingId?buildingId=${buildingId}`,
      providesTags: ["UsersByBuilding"]
    }),
    getUserMetaDataById: builder.query({
      query: (userAuth0Id) => `Users/GetAuth0UserCompleteByAuth0Id?auth0Id=${userAuth0Id}`,
      providesTags: ["UserMetadata"]
    }),
    getUserTenantsById: builder.query({
      query: (id) => `UserTenants/GetByUserId/${id}`,
      providesTags: ["UserTenants"]
    }),
    createUser: builder.mutation({
      query: (payload) => ({
        url: "Users",
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["Users", "UserBuildings"]
    }),
    editUser: builder.mutation({
      query: (payload) => ({
        url: `Users/${payload.id}/Update`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: [
        "User",
        "UserMetadata",
        "Users",
        "UserBuildings",
        "UsersByBuilding",
        "UsersByTenant"
      ]
    }),
    deleteUser: builder.mutation({
      query: (id) => ({
        url: `Users/${id}/Delete`,
        method: "POST"
      }),
      invalidatesTags: ["User", "UserMetadata", "Users"]
    }),
    deleteUserBuilding: builder.mutation({
      query: (payload) => ({
        url: `UserBuildings/Delete?userId=${payload.userId}&buildingId=${payload.buildingId}`,
        method: "POST"
      }),
      invalidatesTags: ["User", "UserMetadata", "Users", "UserBuildings", "UsersByBuilding"]
    }),
    addUserBuilding: builder.mutation({
      query: (payload) => ({
        url: "UserBuildings",
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["User", "UserMetadata", "Users", "UserBuildings", "UsersByBuilding"]
    }),
    updateUserBuilding: builder.mutation({
      query: (payload) => ({
        url: `UserBuildings/Update?userId=${payload.userId}&buildingId=${payload.buildingId}`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["User", "UserMetadata", "Users", "UserBuildings"]
    }),
    updateUserLang: builder.mutation({
      query: (payload) => ({
        url: `Users/${payload.userId}/UpdateLanguage?language=${payload.language}`,
        method: "POST"
      })
    }),
    addUserTenants: builder.mutation({
      query: (payload) => ({
        url: `UserTenants/UpdateUserTenants?userId=${payload[0].userId}`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["User", "UserMetadata", "UserTenants", "Users", "UserBuildings"]
    }),

    //Profiles Queries
    getProfilesByBuildingId: builder.query({
      query: (buildingId) => `Profiles/GetByBuildingId/${buildingId}`,
      providesTags: ["Profiles"]
    }),
    getProfileModulesByProfileId: builder.query({
      query: (profileId) => `ProfileModules/GetByProfileId/${profileId}`,
      providesTags: ["ProfilesModules"]
    }),
    createProfile: builder.mutation({
      query: (payload) => ({
        url: "Profiles",
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["Profiles", "UserBuildings"]
    }),
    updateProfile: builder.mutation({
      query: (payload) => ({
        url: `Profiles/${payload.id}/Update`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["Profiles", "ProfilesModules", "UserBuildings"]
    }),
    updateModulesForProfile: builder.mutation({
      query: (payload) => ({
        url: `ProfileModules/UpdateProfileModules?profileId=${payload[0].profileId}`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["Profiles", "ProfilesModules", "UserBuildings"]
    }),
    deleteProfile: builder.mutation({
      query: (profileId) => ({
        url: `Profiles/${profileId}/Delete`,
        method: "POST"
      }),
      invalidatesTags: ["Profiles", "UserBuildings"]
    }),

    // Conditional Alerts
    getConditionalAlerts: builder.query({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join("&");
        return `ConditionalAlerts/GetByBuildingIdConditionalAlertStatus?${queryString}`;
      },
      providesTags: ["Alerts"]
    }),
    getConditionalAlertsByBuilding: builder.query({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join("&");
        return `ConditionalAlerts/GetByBuildingId?${queryString}`;
      },
      providesTags: ["AlertsBuilding"]
    }),
    getConditionalAlertsSettings: builder.query({
      query: (payload) => {
        const queryString = Object.keys(payload)
          .map((key) => `${key}=${payload[key]}`)
          .join("&");
        return `ConditionalAlertSettings/GetByBuildingId?${queryString}`;
      },
      providesTags: ["AlertsSettings"]
    }),
    createConditionalAlertSettings: builder.mutation({
      query: (payload) => ({
        url: "ConditionalAlertSettings",
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["AlertsSettings"]
    }),
    updateConditionalAlertStatus: builder.mutation({
      query: (payload) => ({
        url: `ConditionalAlertSettings/${payload.id}/UpdateStatus?isEnabled=${payload.enabled}`,
        method: "POST"
      }),
      invalidatesTags: ["AlertsSettings"]
    }),
    updateConditionalAlertSettings: builder.mutation({
      query: (payload) => ({
        url: `ConditionalAlertSettings/${payload.id}/UpdateNested`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["AlertsSettings"]
    }),
    deleteConditionalAlertSettings: builder.mutation({
      query: (id) => ({
        url: `ConditionalAlertSettings/${id}/Delete`,
        method: "POST"
      }),
      invalidatesTags: ["AlertsSettings"]
    }),

    // ESG Reports
    getESGReportsByBuildingId: builder.query({
      query: (filter) => {
        return `SdgReports/GetByBuildingId/${filter.buildingId}`;
      },
      providesTags: ["Reports"]
    }),

    getESGReports: builder.query({
      query: (filter) => {
        return `SdgReports/GetByBuildingId/${filter.buildingId}/${filter.year}`;
      },
      providesTags: ["Reports"]
    }),

    //SensorTypes
    getSensorTypesCatalog: builder.query({
      query: (filter) => {
        return `SensorTypes`;
      },
      providesTags: ["CatalogSensor"]
    }),

    //Notifications
    getNotifications: builder.query({
      query: (tenantId) => {
        return `AppNotifications?tenantId=${tenantId}`;
      },
      providesTags: ["Notifications"]
    }),

    //Flexible Rates
    getFlexibleRates: builder.query({
      query: (payload) => {
        return `FlexibleRates/GetByBuildingId/${payload}`;
      },
      providesTags: ["FlexibleRates"]
    }),
    postFlexibleRates: builder.mutation({
      query: (payload) => ({
        url: "FlexibleRates",
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["FlexibleRates"]
    }),
    postMassiveFlexibleRates: builder.mutation({
      query: (payload) => ({
        url: `FlexibleRates/AddUpdateMassive?buildingId=${payload[0].buildingId}&sensorType=${payload[0].sensorType}`,
        method: "POST",
        body: payload
      }),
      invalidatesTags: ["FlexibleRates"]
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAreasByFloorQuery,
  useGetBuildingWithFloorsByIdQuery,
  useLazyGetAreasByFloorQuery,
  useGetFlexibleRatesQuery,
  usePostMassiveFlexibleRatesMutation,
  usePostFlexibleRatesMutation,
  useDeleteFavoriteMutation,
  useGetFavoritesByUserIdQuery,
  useAddFavoriteMutation,
  useUpdateTenantNameMutation,
  useUpdateUserLangMutation,
  useGetAuth0UsersByBuildingQuery,
  useGetSensorTypesCatalogQuery,
  useUpdateConditionalAlertSettingsMutation,
  useUpdateConditionalAlertStatusMutation,
  useGetConditionalAlertsByBuildingQuery,
  useGetUserTenantsByIdQuery,
  useDeleteConditionalAlertSettingsMutation,
  useCreateConditionalAlertSettingsMutation,
  useUpdateModulesForProfileMutation,
  useUpdateProfileMutation,
  useGetBuildingByUserIdQuery,
  useGetProfileModulesByProfileIdQuery,
  useGetConditionalAlertsSettingsQuery,
  useDeleteUserMutation,
  useUpdateUserBuildingMutation,
  useAddUserTenantsMutation,
  useDeleteUserBuildingMutation,
  useAddUserBuildingMutation,
  useGetTenantsWithBuildingProfilesQuery,
  useGetESGReportsByBuildingIdQuery,
  useGetESGReportsQuery,
  useGetConditionalAlertsQuery,
  useGetBuildingSensorTypesQuery,
  useGetBuildingModulesQuery,
  useDeleteProfileMutation,
  useDeleteBuildingSensorTypeMutation,
  useGetBuildingMetadataByTenantIdQuery,
  useGetUserByIdQuery,
  useEditBuildingMutation,
  useGetTenantsQuery,
  useDeleteBuildingModuleMutation,
  useAddBuildingSensorTypeMutation,
  useAddBuildingModuleMutation,
  useCreateProfileMutation,
  useGetProfilesByBuildingIdQuery,
  useCreateUserMutation,
  useEditUserMutation,
  useGetBuildingByTenantQuery,
  useGetAuth0UsersQuery,
  useGetUserMetaDataByIdQuery,
  useGetAuth0UsersByTenantQuery,
  useGetBuildingByIdQuery,
  useGetNotificationsQuery,
  useUpdateFavoritesMutation
} = enappgyMetaDataServiceApi;
